import CustomHTMLElement from '@onpace/onspace-core/components/html_element'

import translation from '@onpace/onspace-core/components/translations'
import { registerDialogPlayerClass } from '@onpace/onspace-media/elements/player/dialog'

import OnspaceVideoPlayer from '@onpace/onspace-media/elements/player/video'
import OnspaceMultiVideoPlayerControls from '@onpace/onspace-media/elements/player/multi_video/controls'

import playerDialog from '@onpace/onspace-media/elements/player/dialog'

const MAX_PLAYERS_INLINE = 1
const MAX_PLAYERS_COMPACT = 2
const MAX_PLAYERS_NORMAL = 4

const PLAYERS_LAYOUT_GRID = 'grid'
const PLAYERS_LAYOUT_ASYMMETRIC = 'asymmetric'

const PLAYERS_ORIENTATION_PORTRAIT = 'portrait'
const PLAYERS_ORIENTATION_LANSCAPE = 'landscape'

const isTouchScreen = window.matchMedia('(pointer: coarse)').matches

/// The Onspace Multi Screen Video is a component which can display multiple video players.
export default class OnspaceMultiVideoPlayer extends CustomHTMLElement {
  /// Sets up the multi player element.
  ///
  /// This creates the required children and adds events where necessary.
  ///
  /// The element can be configured either from an object passed through the constructor, or as attributes on the
  /// element. The following options are supported:
  /// [items]
  ///   An array of metadata objects. Note that the +id+ field is required, and any items missing one will be ignored.
  ///   These can also be provided as the +items+ attribute.
  ///
  ///   See OnspaceMediaPlayer for more information.
  /// [sourcesLoader]
  ///   An asynchronous function which can load sources. See OnspaceMediaPlayer.loadSources for more information.
  /// [defaultItemId]
  ///   The ID of the video to play by default. If not provided, the first item in +items+ will be used.
  ///
  ///   This can also be provided as the +default-item-id+ attribute.
  /// [tabs]
  ///   An array of tabs which will be displayed alongside the tabs of the player being controlled. Each tab may
  ///   include a +mediaIds+ array, restricting it to those media. See OnspaceMediaPlayer for more information.
  ///
  ///   Tabs will be maintained when the players change, and can be updated using +setTabs+.
  /// [options]
  ///   Additional configuration for the player. The following are available:
  ///   [autoplay]
  ///     When +true+, the media will start as soon as the player enters the DOM. This is +false+ by default.
  ///
  ///     This can also be set by placing an +autoplay+ attribute on the HTML element.
  ///
  ///     Note that most browsers will prevent auto-playing audio unless it was triggered via a user interaction. See
  ///     OnspaceMediaPlayer.resumePlayback for more info.
  ///   [restartAfterFatalError]
  ///     When +true+, the player will automatically restart after a fatal error, after a wait period.
  ///   [hideLivePlaybackControls]
  ///     When +true+, the playback controls will be hidden for live streams.
  ///   [startMuted]
  ///     Indicates if the player should begin playback without audio.
  ///   [hideControls]
  ///     Indicates when the player should display controls. This supports the following values:
  ///     [never]
  ///       Never hide the controls. This is the default.
  ///     [inline]
  ///       Only hide controls when viewing inline. Controls will be available in other contexts such as full screen.
  ///     [always]
  ///       Always hide controls.
  ///   [hideSelectionControls]
  ///     Indicates when the player should display the selection overlay controls. This supports the following values:
  ///     [never]
  ///       Never hide the controls. This is the default.
  ///     [inline]
  ///       Only hide controls when viewing inline. Controls will be available in other contexts such as full screen.
  ///     [always]
  ///       Always hide controls.
  ///   [allowPictureInPicture]
  ///     Indicates if picture in picture should be allowed.
  ///   [includeRequestCredentials]
  ///     Indicates whether credentials will be included for cross-site access control requests. This can either be a
  ///     boolean value which applies to all requests, or it can be a function. The function will be called once for
  ///     each request with the URL as the argument, and should return a boolean value.
  ///
  ///     If not provided, this will be determined automatically using +includeCredentialsForUrl+.
  ///   [accentColor]
  ///     Sets the colour to apply to the player's interface. This supports a hex-format colour value, prefixed with a #.
  ///
  ///     By default, this is blank, inheriting the accent colour from the stylesheet.
  runConstructor(options = {}) {
    super.runConstructor(options)

    this.detectedMaxPlayers = MAX_PLAYERS_INLINE

    this.addEventListener('fullscreenchange', this.fullscreenChanged.bind(this))
    this.resizeObserver = new ResizeObserver(this.resizeObserved.bind(this))
  }

  /// Runs when the multi player is first connected to the DOM.
  ///
  /// If the player is configured to autoplay, this will start the player.
  runFirstConnected(options = {}) {
    super.runFirstConnected()

    this.classList.add('onspace-multi-video')

    this.addPressEventListeners({ onPress: this.pressed.bind(this) })

    const computedStyle = this.getComputedStyle()
    this.breakpointHorizontalCompact = parseInt(computedStyle.getPropertyValue('--onspace-multi-video-breakpoint-compact').replace('px', ''))
    this.breakpointHorizontalNormal = parseInt(computedStyle.getPropertyValue('--onspace-multi-video-breakpoint-normal').replace('px', ''))

    this.items = options.items || this.getJsonAttribute('data-videos') || []
    this.items = this.items.filter((item) => !!item.id)

    this.sourcesLoader = this.sourcesLoader || options.sourcesLoader
    if (!this.sourcesLoader) { throw '`sourcesLoader` is required for the multi video player' }

    const playerOptions = options.options || {}
    const autoplay = playerOptions.autoplay || this.getBooleanAttribute('autoplay')
    this.restartAfterFatalError = playerOptions.restartAfterFatalError || false
    this.hideLivePlaybackControls = playerOptions.hideLivePlaybackControls || false
    const startMuted = playerOptions.startMuted || false
    this.hideControls = playerOptions.hideControls || 'never'
    this.hideSelectionControls = playerOptions.hideSelectionControls || 'never'
    this.allowPictureInPicture = playerOptions.allowPictureInPicture
    this.includeRequestCredentials = options.includeRequestCredentials || null

    if (typeof playerOptions.accentColor === 'string') {
      this.style.setProperty('--player-accent-color', playerOptions.accentColor)
    }

    this.setTabs(options.tabs)

    this.setupContent()

    if (this.items.length === 0) {
      this.showCoverMessage(translation('onspace.media.player.error.source_missing'))
    } else {
      this.setupPlayers()

      const defaultItemId = options.defaultItemId || this.getAttribute('default-item-id') || this.items[0].id
      this.addPlayer(defaultItemId, { autoplay, startMuted })

      this.setupControls()
    }
  }

  /// Runs when the media player is connected to the DOM.
  runConnected() {
    super.runConnected()

    this.resizeObserver.observe(this)
    this.detectMaxPlayers()
    this.detectLayoutOrientation()
    this.detectPlayersLayoutPreference()
  }

  /// Runs when the media player is disconnected from the DOM.
  ///
  /// This cleans up events relating to the document.
  runDisconnected() {
    super.runDisconnected()

    this.resizeObserver.unobserve(this)
  }

  /// Responds to changes in the element's size.
  resizeObserved(_entries) {
    this.detectMaxPlayers()
    this.detectLayoutOrientation()
  }

  ////////// Interactions

  /// Responds to clicks and touches on the controls.
  pressed(event) {
    const selectedPlayer = this.selectedPlayerForEvent(event)
    if (selectedPlayer) { this.activatePlayer(selectedPlayer.multiScreenId) }
  }

  ////////// Elements

  /// Sets up the content element.
  setupContent() {
    this.contentElement = document.createElement('div')
    this.contentElement.classList.add('onspace-player__content')

    this.appendChild(this.contentElement)
  }

  ////////// Players

  /// Initialises and configures the players container element.
  setupPlayers() {
    if (this.playersContainerElement) { return }

    this.playersContainerElement = document.createElement('div')
    this.playersContainerElement.classList.add('onspace-multi-video__players')
    this.contentElement.appendChild(this.playersContainerElement)

    this.players = []
  }

  /// Retrieves the player which is currently active.
  get activePlayer() {
    return this._activePlayer
  }

  /// Sets a player as the active player.
  set activePlayer(value) {
    if (this.activePlayer === value) { return }

    let existingVolume = null
    if (this.activePlayer) {
      existingVolume = this.activePlayer.audioVolume
      this.activePlayer.disableAudio()

      this.activePlayer.parentElement.classList.remove('onspace-multi-video__player--active')
    }

    this._activePlayer = value

    value.parentElement.classList.add('onspace-multi-video__player--active')

    if (typeof existingVolume === 'number' && existingVolume > 0) {
      value.audioVolume = existingVolume
    }

    if (this.controlsElement) { this.controlsElement.attachPlayer(value) }
  }

  /// Indicates if there is more than 1 player.
  get hasMultiplePlayers() {
    return this.players.length > 1
  }

  /// Indicates if an additional player can be added.
  get canAddPlayer() {
    if (!this.activePlayer) { return false }
    return this.activePlayer.presentationMode !== 'pictureinpicture'
  }

  /// Indicates if an existing player can be removed.
  get canRemovePlayer() {
    return this.hasMultiplePlayers
  }

  /// Adds or removes the player with the given +itemId+.
  togglePlayer(itemId) {
    const existingPlayer = this.players.find((player) => player.multiScreenId === itemId)

    if (existingPlayer) {
      this.removePlayer(itemId)
    } else {
      this.addPlayer(itemId)
    }
  }

  /// Adds a player with the given +itemId+.
  addPlayer(itemId, { autoplay = true, startMuted = false } = {}) {
    if (!this.canAddPlayer && this.players.length > 0) { return }
    if (this.players.length >= this.maxPlayers) {
      const translationKey = this.maxPlayers === 1 ? 'onspace.media.player.multi_screen.max_limit.singular' : 'onspace.media.player.multi_screen.max_limit.plural'
      const message = translation(translationKey).replace('%{limit}', this.maxPlayers)
      this.showOverlayMessage(message, { icon: 'onspace/icon_warning', timeout: 5000 })
      return
    }

    const metadata = this.items.find((item) => item.id == itemId)
    if (!metadata) { return console.error(`Unable to find item with ID ${itemId}`) } // eslint-disable-line no-console

    if (this.activePlayer && !this.activePlayer.playbackStarted) {
      this.activePlayer.multiScreenId = metadata.id
      this.activePlayer.replaceMedia({ metadata })

      this.playersStateChanged()
      this.triggerEvent('onspace:media:multi-video:players-changed')
      return
    }

    let player
    if (playerDialog.playerPersisted && playerDialog.persistedPlayer.persistenceId.startsWith('multi-screen--')) {
      player = playerDialog.persistedPlayer
      player.multiPlayer = this
    } else {
      player = new OnspaceVideoPlayer({
        sourcesLoader: this.sourcesLoader,
        metadata,
        options: {
          persistenceId: `multi-screen--${metadata.id}`,
          autoplay,
          restartAfterFatalError: this.restartAfterFatalError,
          hideLivePlaybackControls: this.hideLivePlaybackControls,
          startMuted,
          includeRequestCredentials: this.includeRequestCredentials
        },
        multiPlayer: this
      })
    }

    player.multiScreenId = metadata.id
    player.addEventListener('onspace:media:player:playback-started', this.playerPlaybackStarted.bind(this))
    player.addEventListener('onspace:media:player:fatal-error', this.playerFatallyErrored.bind(this))
    player.addEventListener('onspace:media:player:media-changed', this.playerMediaChanged.bind(this))

    const playerWrapper = document.createElement('div')
    playerWrapper.classList.add('onspace-multi-video__player-wrapper')
    playerWrapper.appendChild(player)

    const playerIndexElement = document.createElement('div')
    playerIndexElement.classList.add('onspace-multi-video__player__index')
    playerWrapper.appendChild(playerIndexElement)
    player.setIndex = (index) => playerIndexElement.innerText = index.toString()

    this.players.push(player)
    this.playersContainerElement.appendChild(playerWrapper)

    this.activePlayer = player

    this.playersStateChanged()
    this.triggerEvent('onspace:media:multi-video:players-changed')
  }

  /// Removes the player with the given +itemId+.
  removePlayer(itemId) {
    if (!this.canRemovePlayer) { return }

    const existingPlayerIndex = this.players.findIndex((player) => player.multiScreenId === itemId)
    if (existingPlayerIndex < 0) { return }

    const existingPlayer = this.players[existingPlayerIndex]
    const playerWrapper = existingPlayer.parentElement

    this.players.splice(existingPlayerIndex, 1)

    if (this.activePlayer === existingPlayer) {
      this.activePlayer = this.players[0]
    }

    playerWrapper.remove()

    this.playersStateChanged()
    this.triggerEvent('onspace:media:multi-video:players-changed')
  }

  /// Activates the player with the given +itemId+.
  activatePlayer(itemId) {
    const existingPlayer = this.players.find((player) => player.multiScreenId === itemId)
    if (!existingPlayer) { return }

    if (existingPlayer.playbackStarted || existingPlayer.fatalError) {
      this.activePlayer = existingPlayer
    } else {
      existingPlayer.startPlayer()
    }

    this.playersStateChanged()
    this.triggerEvent('onspace:media:multi-video:players-changed')
  }

  /// Replaces the media in an existing player with the given +itemId+.
  ///
  /// This will replace the player identified by +currentItemId+. If it is not provided or cannot be found, the active
  /// player will be replaced.
  replacePlayer(itemId, currentItemId) {
    const existingPlayer = this.players.find((player) => player.multiScreenId === currentItemId) || this.activePlayer
    if (!existingPlayer) { return }

    const metadata = this.items.find((item) => item.id == itemId)
    if (!metadata) { return console.error(`Unable to find item with ID ${itemId}`) } // eslint-disable-line no-console

    existingPlayer.multiScreenId = metadata.id
    existingPlayer.replaceMedia({ metadata })

    this.playersStateChanged()
    this.triggerEvent('onspace:media:multi-video:players-changed')
  }

  /// Notifies all players that they should update their presentation state.
  playersStateChanged() {
    this.playersContainerElement.setAttribute('data-players', this.players.length)

    const closeAction = this.canRemovePlayer || this.fullscreenActive ? this.playerClosePressed.bind(this) : null

    this.players.forEach((player, index) => {
      player.closeAction = closeAction
      player.setIndex(index + 1)
    })
    this.triggerEvent('onspace:media:multi-video:items-changed')
    this.setupControls()
  }

  /// Attempts to locate a player which was selected by the given event.
  ///
  /// This finds a player which contains the event's location coordinates, or returns +null+ if one cannot be found.
  selectedPlayerForEvent(event) {
    const selectedPlayer = this.players.find((player) => {
      const playerRect = player.getBoundingClientRect()
      return playerRect.left <= event.clientX && event.clientX <= playerRect.right
        && playerRect.top <= event.clientY && event.clientY <= playerRect.bottom
    })

    return selectedPlayer || null
  }

  /// Responds to a player activating its close action.
  playerClosePressed() {
    if (this.canRemovePlayer) {
      this.removePlayer(this.activePlayer.multiScreenId)
    } else {
      if (this.fullscreenActive) { this.exitFullscreen() }
    }
  }

  /// Responds to a player starting playback.
  playerPlaybackStarted(_event) {
    this.setupControls()
  }

  /// Responds to a player experiencing a fatal error during playback.
  playerFatallyErrored(_event) {
    this.setupControls()
  }

  /// Responds to a player changing the media it is presenting.
  ///
  /// This keeps the player's multi screen ID aligned with its current media.
  playerMediaChanged(event) {
    const player = event.currentTarget

    const newMediaId = player.metadata?.id
    if (newMediaId == null || newMediaId == player.multiScreenId) { return }

    player.multiScreenId = newMediaId

    this.playersStateChanged()
    this.triggerEvent('onspace:media:multi-video:players-changed')
  }

  ///// Layout

  get overriddenMaxPlayers() {
    return this._overriddenMaxPlayers || null
  }

  set overriddenMaxPlayers(value) {
    this._overriddenMaxPlayers = value
    this.detectMaxPlayers()
  }

  /// Retrieves the maximum number of players that can be shown.
  get maxPlayers() {
    return this.overriddenMaxPlayers || this.detectedMaxPlayers
  }

  /// Detects the maximum number of players for the size of the element.
  ///
  /// This is called automatically.
  detectMaxPlayers() {
    const maxPlayers = this.maxPlayers

    const clientRect = this.getBoundingClientRect()
    if (clientRect.width < this.breakpointHorizontalCompact && clientRect.height < this.breakpointHorizontalCompact) {
      this.detectedMaxPlayers = MAX_PLAYERS_INLINE
    } else if ((clientRect.width > this.breakpointHorizontalNormal && clientRect.height > this.breakpointHorizontalCompact)
      || (clientRect.height > this.breakpointHorizontalNormal && clientRect.width > this.breakpointHorizontalCompact)) {
      this.detectedMaxPlayers = MAX_PLAYERS_NORMAL
    } else {
      this.detectedMaxPlayers = MAX_PLAYERS_COMPACT
    }

    if (this.players.length > this.maxPlayers) {
      const extraPlayers = this.players.slice(this.maxPlayers)
      extraPlayers.forEach((player) => this.removePlayer(player.multiScreenId))

      const translationKey = this.maxPlayers === 1 ? 'onspace.media.player.multi_screen.max_limit.singular' : 'onspace.media.player.multi_screen.max_limit.plural'
      const message = translation(translationKey).replace('%{limit}', this.maxPlayers)
      this.showOverlayMessage(message, { icon: 'onspace/icon_warning', timeout: 5000 })
    } else if (this.maxPlayers != maxPlayers) {
      this.triggerEvent('onspace:media:multi-video:players-changed')
    }
  }

  /// Detects the orientation of the player.
  ///
  /// This is called automatically when the size of the element is changed.
  detectLayoutOrientation() {
    if (this.playersOrientation) {
      this.playersContainerElement.classList.remove(`onspace-multi-video__players--${this.playersOrientation}`)
    }

    const clientRect = this.getBoundingClientRect()
    if (clientRect.width * 1.2 < clientRect.height) {
      this.playersOrientation = PLAYERS_ORIENTATION_PORTRAIT
    } else {
      this.playersOrientation = PLAYERS_ORIENTATION_LANSCAPE
    }

    this.playersContainerElement.classList.add(`onspace-multi-video__players--${this.playersOrientation}`)
  }

  /// Detects which players layout to use.
  ///
  /// When the layout is changed, that preference is set in +localStorage+. This retrieves that preference and will
  /// re-enable that same layout.
  detectPlayersLayoutPreference() {
    const preference = localStorage.getItem('onspace-multi-video-layout') || PLAYERS_LAYOUT_GRID
    this.playersLayout = preference
  }

  /// Cycles through the players layouts.
  togglePlayersLayout() {
    switch (this.playersLayout) {
    case PLAYERS_LAYOUT_GRID:
      this.playersLayout = PLAYERS_LAYOUT_ASYMMETRIC
      break
    case PLAYERS_LAYOUT_ASYMMETRIC:
      this.playersLayout = PLAYERS_LAYOUT_GRID
      break
    }
  }

  /// Retrieves the current layout for players.
  get playersLayout() {
    return this._playersLayout
  }

  /// Sets the layout for players.
  ///
  /// If the number of active players is more than can be shown in the layout, some players will be removed.
  set playersLayout(value) {
    if (this.playersLayout === value) { return }

    if (this.playersLayout) {
      this.playersContainerElement.classList.remove(`onspace-multi-video__players--${this.playersLayout}`)
    }

    this._playersLayout = value
    localStorage.setItem('onspace-multi-video-layout', value)

    this.playersContainerElement.classList.add(`onspace-multi-video__players--${this.playersLayout}`)

    this.triggerEvent('onspace:media:multi-video:players-changed')
  }

  ////////// Controls

  /// Initialises and configures the controls element.
  setupControls() {
    if (!this.controlsElement) {
      this.controlsElement = new OnspaceMultiVideoPlayerControls({ player: this.activePlayer, multiPlayer: this })
    }

    if (this.shouldDetachControls) {
      this.controlsElement.tabsElement.selectedTabIndex = null
      this.controlsElement.remove()
    } else {
      this.contentElement.appendChild(this.controlsElement)
    }
  }

  /// Indicates if the controls element should be removed.
  get shouldDetachControls() {
    if (this.activePlayer.fatalError) { return true }

    switch (this.hideControls) {
    case 'always': return this.playbackStarted
    case 'inline': return !this.fullscreenActive && this.playbackStarted
    case 'never':
    default:
      return false
    }
  }

  /// Indicates if the player can be closed.
  get hasCloseAction() {
    return this.fullscreenActive && isTouchScreen
  }

  /// Closes the player if possible.
  performCloseAction() {
    if (!this.fullscreenActive || !isTouchScreen) { return }

    this.exitFullscreen()
  }

  ////////// Tabs

  /// Retrieves the data for the current tabs.
  ///
  /// These are displayed by the controls, in addition to the tabs of the player being controlled.
  get tabData() {
    return this._tabData || []
  }

  /// Prepares the tab data for the multi player.
  ///
  /// Any tab provided without +mediaIds+ is given an empty array, so every tab exposes the attribute.
  setTabs(tabs) {
    this._tabData = tabs

    this.tabData.forEach((tab) => {
      if (tab && !tab.mediaIds) { tab.mediaIds = [] }
    })

    this.triggerEvent('onspace:media:multi-video:tabs-changed')
  }

  ////////// Selection

  get items() {
    return this._items
  }

  set items(value) {
    this._items = value
    this.triggerEvent('onspace:media:multi-video:items-changed')
  }

  /// Indicates whether the user can choose other media to modify the players configuration.
  get canSelectMedia() {
    if (this.playerPictureInPictureActive || this.maxPlayers <= 1) { return false }

    switch (this.hideSelectionControls) {
    case 'always': return false
    case 'inline': return this.fullscreenActive
    case 'never':
    default:
      return true
    }
  }

  ////////// Google Cast

  /// Indicates if a player is allowed to use Google Cast.
  get playerCanGoogleCast() {
    return this.players.length === 1 && !this.fullscreenActive
  }

  ////////// Presentation

  /// Indicates if a player is allowed to enter picture in picture mode.
  get playerCanEnterPictureInPicture() {
    return this.players.length === 1 && !this.fullscreenActive
  }

  /// Indicates if a player is currently using picture in picture.
  get playerPictureInPictureActive() {
    return !this.activePlayer || this.activePlayer.pictureInPictureActive
  }

  ///// Full Screen

  /// Indicates if the browser supports taking any element full screen.
  get supportsDocumentFullscreen() {
    return document.fullscreenEnabled
  }

  /// Indicates if the video supports entering full screen mode.
  get supportsVideoFullscreen() {
    return true
  }

  /// Indicates if the element can currently enter full screen mode.
  get canEnterFullscreen() {
    if (this.playerPictureInPictureActive || this.fullscreenActive) { return false }

    return this.supportsDocumentFullscreen || (this.supportsVideoFullscreen && this.players.length === 1)
  }

  /// Indicates if the element is currently in full screen mode.
  get fullscreenActive() {
    return !!document.fullscreenElement
  }

  /// Enters or exits full screen mode, depending on the current state.
  toggleFullscreen() {
    if (this.fullscreenActive) {
      this.exitFullscreen()
    } else {
      this.enterFullscreen()
    }
  }

  /// Enters the element into full screen mode.
  enterFullscreen() {
    if (!this.canEnterFullscreen) { return }

    if (this.supportsDocumentFullscreen) {
      this.requestFullscreen()
    } else {
      this.activePlayer.nativePlayer.requestFullscreen()
    }
  }

  /// Exits the element from full screen mode.
  exitFullscreen() {
    document.exitFullscreen()
  }

  /// Responds to full screen changes on the element.
  fullscreenChanged(_event) {
    this.playersStateChanged()
    this.triggerEvent('onspace:media:multi-video:presentation-changed')
  }

  ////////// Messages

  /// Removes any messages that are currently displayed.
  clearMessages() {
    if (this.overlayMessage) {
      this.overlayMessage.remove()
      this.overlayMessage = null
    }

    if (this.coverMessage) {
      this.coverMessage.remove()
      this.coverMessage = null
    }
  }

  /// Displays a messages over the element.
  ///
  /// This should be used to display information to a user. This accepts the following options:
  /// [icon]
  ///   An optional icon to be shown with the message.
  /// [timeout]
  ///   An optional duration in milliseconds to hide the message after.
  showOverlayMessage(message, options = {}) {
    this.overlayMessage = document.createElement('div')
    this.overlayMessage.classList.add('onspace-multi-video__overlay-message')

    if (options.icon) {
      const iconElement = SVGElement.createOnspaceSpritemapSvg(options.icon)
      this.overlayMessage.appendChild(iconElement)
    }

    const messageElement = document.createElement('p')
    messageElement.innerText = message
    this.overlayMessage.appendChild(messageElement)

    this.contentElement.appendChild(this.overlayMessage)

    if (options.timeout) {
      window.setTimeout(() => { this.clearMessages() }, options.timeout)
    }
  }

  /// Displays a message that covers the entire player.
  ///
  /// This should be used to display information relating to an error which caused the player to stop completely. Prior,
  /// to calling this, any active player and other elements should be cleaned up. This accepts the following options:
  /// [icon]
  ///   An optional icon to be shown with the message. By default, this will be set to +onspace/icon_warning+.
  /// [actions]
  ///   An optional array of actions to be shown with the message. This should be an array of objects, each containing a
  ///   +title+ and +callback+.
  ///
  /// Note that the cover message is always appended to +this+, and not the +contentElement+.
  showCoverMessage(message, options = {}) {
    this.clearMessages()

    this.coverMessage = document.createElement('div')
    this.coverMessage.classList.add('onspace-player-cover-message')

    const icon = options.icon || 'onspace/icon_warning'
    const iconElement = SVGElement.createOnspaceSpritemapSvg(icon)
    this.coverMessage.appendChild(iconElement)

    const messageElement = document.createElement('p')
    messageElement.innerText = message
    this.coverMessage.appendChild(messageElement)

    if (options.actions && options.actions.length > 0) {
      const actionsElement = document.createElement('div')
      actionsElement.classList.add('onspace-player-cover-message__actions')

      options.actions.forEach((action) => {
        const actionElement = document.createElement('div')
        actionElement.classList.add('onspace-button')
        actionElement.innerText = action.title
        actionElement.addEventListener('click', action.callback)

        actionsElement.appendChild(actionElement)
      })

      this.coverMessage.appendChild(actionsElement)
    }

    this.contentElement.appendChild(this.coverMessage)
  }
}

window.customElements.define('onspace-multi-video', OnspaceMultiVideoPlayer)
registerDialogPlayerClass('multiVideo', OnspaceMultiVideoPlayer)
